import React, { useState } from 'react';
import { Box, Button, Typography, Alert, CircularProgress, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, TextField } from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useAppDispatch } from '../store';
import { addLoadEntry } from '../services/loadService';
import { InvoiceItem, defaultInvoiceItem } from '../config/types';
import * as XLSX from 'xlsx';
import { calculateFreightRev, calculateFuelSurcharge, calculateDemurrageCharge, calculateTotalCharges } from '../config/calculations';

const formatCurrency = (value: number | string) => {
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(num)) return '';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(num);
};

const UploadEntries: React.FC = () => {
  const dispatch = useAppDispatch();
  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [entries, setEntries] = useState<InvoiceItem[]>([]);
  const [editingEntry, setEditingEntry] = useState<{ index: number; entry: InvoiceItem } | null>(null);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (selectedFile) {
      if (selectedFile.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' || 
          selectedFile.type === 'application/vnd.ms-excel') {
        setFile(selectedFile);
        setError(null);
        readExcelFile(selectedFile);
      } else {
        setError('Please upload an Excel file (.xlsx or .xls)');
        setFile(null);
      }
    }
  };

  const readExcelFile = async (file: File) => {
    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data);
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      
      // Set range to start from row 4 (A4)
      const range = XLSX.utils.decode_range(worksheet['!ref'] || 'A1');
      range.s.r = 3; // 0-based index, so 3 means row 4
      worksheet['!ref'] = XLSX.utils.encode_range(range);
      
      // Read with header row
      const jsonData = XLSX.utils.sheet_to_json(worksheet, { 
        header: 1,
        raw: false,
        defval: ''
      });

      console.log('Raw Excel Data:', jsonData);

      // Get headers from row 4 (index 0 after our range adjustment)
      const headers = jsonData[0] as string[];
      console.log('Headers:', headers);

      // Convert the data to InvoiceItem format and filter out empty rows
      const convertedEntries = jsonData
        // .slice(1) // Skip header row
        .filter((row: any) => {
          console.log('Checking row:', row);
          // Find the index of Destination column
          const destIndex = 0;
          console.log('Destination index:', destIndex);
          console.log('Row at destination index:', row[destIndex]);
          
          // Check if the row has a destination value
          const hasDestination = 
                               row[destIndex] !== undefined && 
                               row[destIndex] !== null && 
                               row[destIndex].toString().trim() !== '';
          
          console.log('Has destination:', hasDestination);
          return hasDestination;
        })
        .map((row: any) => {
          console.log('Processing row:', row);
          const getValue = (headerName: string) => {
            const index = headers.findIndex((h: string) => h === headerName);
            if (index === -1) return '';
            
            const value = row[index];
            if (!value) return '';

            // Handle currency values (remove $ and spaces)
            if (typeof value === 'string' && value.includes('$')) {
              const cleanValue = value.replace(/[$\s-]/g, '');
              return cleanValue || '0';
            }

            return value;
          };

          // Get base values
          const gross = row[8];
          const net = row[9];
          const rate =row[10];
          const fuelSurchargePercent = typeof row[11] === 'string'
            ? parseFloat(row[11].replace('%', '').trim()) / 10
            : 0;
          const totalDem = row[24];

          // Calculate financial values
          const freightRev = calculateFreightRev(gross, rate);
          const fuelSurcharge = calculateFuelSurcharge(gross, fuelSurchargePercent);
          const demurrageCharge = calculateDemurrageCharge(totalDem);
          const totalCharges = calculateTotalCharges(freightRev, fuelSurcharge, demurrageCharge);

          console.log('Gross:', gross);
          console.log('Rate:', rate);
          console.log('Fuel Surcharge Percent:', fuelSurchargePercent);
          console.log('Total DEM:', totalDem);
          console.log('Freight Rev:', freightRev);
          console.log('Fuel Surcharge:', fuelSurcharge);
          console.log('Demurrage Charge:', demurrageCharge);
          console.log('Total Charges:', totalCharges);

          const entry = {
            ...defaultInvoiceItem,
            destination: row[0],
            date: row[1],
            bolNumber: row[2],
            invoiceNumber: row[3],
            orderNumber: row[4],
            loadNumber: row[5],
            terminal: row[6],
            broker: row[7],
            gross: row[8],
            net: row[9],
            rate: row[10],
            fuelSurchargePercent: row[11],
            terminalArrivalTime: row[14],
            terminalDepartTime: row[15],
            deliveryArrivalTime: row[18],
            deliveryDepartTime: row[19],
            driver: row[21],
            notes: row[28],
            totalDem: row[24],
            freightRev,
            fuelSurcharge: fuelSurcharge.toString(),
            demurrageCharge,
            totalCharges
          };
          console.log('Converted entry:', entry);
          return entry;
        });

      console.log('Final converted entries:', convertedEntries);
      setEntries(convertedEntries);
    } catch (err) {
      console.error('Error reading Excel file:', err);
      setError('Failed to read Excel file');
    }
  };

  const handleUpload = async () => {
    if (entries.length === 0) {
      setError('No entries to upload');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      // Save all entries to Firebase
      const savePromises = entries.map(entry => {
        // Ensure all required fields are present
        if (!entry.broker || !entry.destination || !entry.date || !entry.bolNumber || 
            !entry.loadNumber || !entry.terminal || !entry.gross || !entry.rate || !entry.driver) {
          throw new Error(`Entry ${entry.loadNumber} is missing required fields`);
        }
        return addLoadEntry(entry);
      });

      await Promise.all(savePromises);
      
      setSuccess(`${entries.length} entries uploaded successfully`);
      setEntries([]);
      setFile(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to upload entries');
    } finally {
      setLoading(false);
    }
  };

  const handleEditEntry = (index: number) => {
    setEditingEntry({ index, entry: entries[index] });
  };

  const handleDeleteEntry = (index: number) => {
    setEntries(prev => prev.filter((_, i) => i !== index));
  };

  const handleSaveEdit = () => {
    if (editingEntry) {
      setEntries(prev => prev.map((entry, index) => 
        index === editingEntry.index ? editingEntry.entry : entry
      ));
      setTimeout(() => setEditingEntry(null), 0);
    }
  };

  const handleCloseEditDialog = () => {
    setTimeout(() => setEditingEntry(null), 0);
  };

  const handleEditChange = (field: keyof InvoiceItem, value: string) => {
    if (editingEntry) {
      setEditingEntry(prev => ({
        ...prev!,
        entry: {
          ...prev!.entry,
          [field]: value
        }
      }));
    }
  };

  return (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column', 
      alignItems: 'center',
      gap: 3,
      p: 3
    }}>
      <Typography variant="h6" gutterBottom>
        Upload Entries from Excel
      </Typography>

      <Box sx={{ 
        border: '2px dashed #ccc',
        borderRadius: 2,
        p: 3,
        width: '100%',
        maxWidth: 600,
        textAlign: 'center'
      }}>
        <input
          accept=".xlsx,.xls"
          style={{ display: 'none' }}
          id="upload-file"
          type="file"
          onChange={handleFileChange}
        />
        <label htmlFor="upload-file">
          <Button
            component="span"
            variant="contained"
            startIcon={<CloudUploadIcon />}
            disabled={loading}
          >
            Select File
          </Button>
        </label>
        {file && (
          <Typography sx={{ mt: 2 }}>
            Selected file: {file.name}
          </Typography>
        )}
      </Box>

      {entries.length > 0 && (
        <TableContainer 
          component={Paper} 
          sx={{ 
            width: '100%', 
            maxWidth: 1200,
            maxHeight: '60vh',
            overflow: 'auto'
          }}
        >
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Destination</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>BOL Number</TableCell>
                <TableCell>Invoice Number</TableCell>
                <TableCell>Order Number</TableCell>
                <TableCell>Load Number</TableCell>
                <TableCell>Terminal</TableCell>
                <TableCell>Broker</TableCell>
                <TableCell>Gross</TableCell>
                <TableCell>Net</TableCell>
                <TableCell>Rate</TableCell>
                <TableCell>Fuel Surcharge %</TableCell>
                <TableCell>Freight Rev</TableCell>
                <TableCell>Fuel Surcharge</TableCell>
                <TableCell>Terminal Arrival</TableCell>
                <TableCell>Terminal Depart</TableCell>
                <TableCell>Delivery Arrival</TableCell>
                <TableCell>Delivery Depart</TableCell>
                <TableCell>Driver</TableCell>
                <TableCell>Total DEM</TableCell>
                <TableCell>Demurrage</TableCell>
                <TableCell>Total Charges</TableCell>
                <TableCell>Notes</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {entries.map((entry, index) => (
                <TableRow key={index}>
                  <TableCell>{entry.destination}</TableCell>
                  <TableCell>{entry.date}</TableCell>
                  <TableCell>{entry.bolNumber}</TableCell>
                  <TableCell>{entry.invoiceNumber}</TableCell>
                  <TableCell>{entry.orderNumber}</TableCell>
                  <TableCell>{entry.loadNumber}</TableCell>
                  <TableCell>{entry.terminal}</TableCell>
                  <TableCell>{entry.broker}</TableCell>
                  <TableCell>{entry.gross}</TableCell>
                  <TableCell>{entry.net}</TableCell>
                  <TableCell>{entry.rate}</TableCell>
                  <TableCell>{entry.fuelSurchargePercent}</TableCell>
                  <TableCell>{formatCurrency(entry.freightRev)}</TableCell>
                  <TableCell>{formatCurrency(entry.fuelSurcharge)}</TableCell>
                  <TableCell>{entry.terminalArrivalTime}</TableCell>
                  <TableCell>{entry.terminalDepartTime}</TableCell>
                  <TableCell>{entry.deliveryArrivalTime}</TableCell>
                  <TableCell>{entry.deliveryDepartTime}</TableCell>
                  <TableCell>{entry.driver}</TableCell>
                  <TableCell>{entry.totalDem}</TableCell>
                  <TableCell>{formatCurrency(entry.demurrageCharge)}</TableCell>
                  <TableCell>{formatCurrency(entry.totalCharges)}</TableCell>
                  <TableCell>{entry.notes}</TableCell>
                  <TableCell>
                    <IconButton onClick={() => handleEditEntry(index)}>
                      <EditIcon />
                    </IconButton>
                    <IconButton onClick={() => handleDeleteEntry(index)}>
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Button
        variant="contained"
        color="primary"
        onClick={handleUpload}
        disabled={entries.length === 0 || loading}
        sx={{ minWidth: 200 }}
      >
        {loading ? <CircularProgress size={24} /> : `Upload ${entries.length} Entries`}
      </Button>

      {error && (
        <Alert severity="error" sx={{ width: '100%', maxWidth: 600 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ width: '100%', maxWidth: 600 }}>
          {success}
        </Alert>
      )}

      <Dialog
        open={!!editingEntry}
        onClose={handleCloseEditDialog}
        maxWidth="md"
        fullWidth
        disableRestoreFocus
      >
        <DialogTitle>Edit Entry</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
            <TextField label="Destination" value={editingEntry?.entry.destination || ''} onChange={e => handleEditChange('destination', e.target.value)} />
            <TextField label="Date" type="date" value={editingEntry?.entry.date || ''} onChange={e => handleEditChange('date', e.target.value)} InputLabelProps={{ shrink: true }} />
            <TextField label="BOL Number" value={editingEntry?.entry.bolNumber || ''} onChange={e => handleEditChange('bolNumber', e.target.value)} />
            <TextField label="Invoice Number" value={editingEntry?.entry.invoiceNumber || ''} onChange={e => handleEditChange('invoiceNumber', e.target.value)} />
            <TextField label="Order Number" value={editingEntry?.entry.orderNumber || ''} onChange={e => handleEditChange('orderNumber', e.target.value)} />
            <TextField label="Load Number" value={editingEntry?.entry.loadNumber || ''} onChange={e => handleEditChange('loadNumber', e.target.value)} />
            <TextField label="Terminal" value={editingEntry?.entry.terminal || ''} onChange={e => handleEditChange('terminal', e.target.value)} />
            <TextField label="Broker" value={editingEntry?.entry.broker || ''} onChange={e => handleEditChange('broker', e.target.value)} />
            <TextField label="Gross" value={editingEntry?.entry.gross || ''} onChange={e => handleEditChange('gross', e.target.value)} />
            <TextField label="Net" value={editingEntry?.entry.net || ''} onChange={e => handleEditChange('net', e.target.value)} />
            <TextField label="Rate" value={editingEntry?.entry.rate || ''} onChange={e => handleEditChange('rate', e.target.value)} />
            <TextField label="Fuel Surcharge %" value={editingEntry?.entry.fuelSurchargePercent || ''} onChange={e => handleEditChange('fuelSurchargePercent', e.target.value)} />
            <TextField label="Terminal Arrival" value={editingEntry?.entry.terminalArrivalTime || ''} onChange={e => handleEditChange('terminalArrivalTime', e.target.value)} />
            <TextField label="Terminal Depart" value={editingEntry?.entry.terminalDepartTime || ''} onChange={e => handleEditChange('terminalDepartTime', e.target.value)} />
            <TextField label="Delivery Arrival" value={editingEntry?.entry.deliveryArrivalTime || ''} onChange={e => handleEditChange('deliveryArrivalTime', e.target.value)} />
            <TextField label="Delivery Depart" value={editingEntry?.entry.deliveryDepartTime || ''} onChange={e => handleEditChange('deliveryDepartTime', e.target.value)} />
            <TextField label="Driver" value={editingEntry?.entry.driver || ''} onChange={e => handleEditChange('driver', e.target.value)} />
            <TextField label="Total DEM" value={editingEntry?.entry.totalDem || ''} onChange={e => handleEditChange('totalDem', e.target.value)} />
            <TextField label="Notes" value={editingEntry?.entry.notes || ''} onChange={e => handleEditChange('notes', e.target.value)} />

            {/* Calculated fields (read-only) */}
            <TextField label="Freight Rev" value={formatCurrency(editingEntry?.entry.freightRev ?? 0)} InputProps={{ readOnly: true }} />
            <TextField label="Fuel Surcharge" value={formatCurrency(editingEntry?.entry.fuelSurcharge ?? 0)} InputProps={{ readOnly: true }} />
            <TextField label="Demurrage" value={formatCurrency(editingEntry?.entry.demurrageCharge ?? 0)} InputProps={{ readOnly: true }} />
            <TextField label="Total Charges" value={formatCurrency(editingEntry?.entry.totalCharges ?? 0)} InputProps={{ readOnly: true }} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditDialog}>Cancel</Button>
          <Button onClick={handleSaveEdit} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UploadEntries; 